import type {
    MountGuideCaptureMaterial,
    MountGuideDetails,
    MountGuideDropSource,
    MountGuideMissionHint,
    MountGuideNote,
    MountGuideSkill,
    MountGuideStatRow,
} from '../types';

export const DEFAULT_MOUNT_DETAILS: MountGuideDetails = {
    grade: 1,
    monsterLevel: 1,
    fellowLevelMin: 1,
    fellowLevelMax: 25,
    moveTypeEn: 'Ground',
    moveTypePtBr: 'Terrestre',
    capturePoints: 1,
    captureConditions: [''],
    spawnAreaEn: '',
    spawnAreaPtBr: '',
    spawnAltitude: '',
    spawnBehaviorPtBr: '',
    spawnBehaviorEn: '',
    mapImageUrl: '',
    mapMarker: { x: 0.5, y: 0.5 },
    notes: [],
    stats: [
        { key: 'hp', labelEn: 'HP', labelPtBr: 'HP', lv1: '0', lvMax: '0' },
        { key: 'energy', labelEn: 'Willpower', labelPtBr: 'Força de vontade', lv1: '0', lvMax: '0' },
    ],
    statsNoteEn: '',
    statsNotePtBr: '',
    skills: [],
    activeSkills: [],
    sealStone: [],
    captureMaterials: [],
    dropSources: [],
    missionHints: [],
};

export function parseMountDetails(json?: string | null): MountGuideDetails {
    if (!json?.trim()) return structuredClone(DEFAULT_MOUNT_DETAILS);
    try {
        const parsed = JSON.parse(json) as MountGuideDetails;
        return {
            ...structuredClone(DEFAULT_MOUNT_DETAILS),
            ...parsed,
            captureConditions: parsed.captureConditions?.length ? parsed.captureConditions : [''],
            mapMarker: parsed.mapMarker ?? { x: 0.5, y: 0.5 },
        };
    } catch {
        return structuredClone(DEFAULT_MOUNT_DETAILS);
    }
}

export function stringifyMountDetails(details: MountGuideDetails): string {
    return JSON.stringify(details, null, 2);
}

export function detailsFromJson(json: string): MountGuideDetails {
    return parseMountDetails(json);
}

export function jsonFromDetails(details: MountGuideDetails): string {
    return stringifyMountDetails(details);
}

export const BARTOLO_DETAILS: MountGuideDetails = {
    grade: 4,
    monsterLevel: 70,
    fellowLevelMin: 1,
    fellowLevelMax: 70,
    moveTypeEn: 'Air',
    moveTypePtBr: 'Ar',
    capturePoints: 60,
    captureConditions: ['Prova da Besta Demoníaca Bartolo', 'Reduzir HP'],
    spawnAreaEn: 'Brakal Forest',
    spawnAreaPtBr: 'Floresta de Brakal',
    spawnAltitude: '-',
    spawnBehaviorEn: 'Patrolling in intervals of approximately 80 meters.',
    spawnBehaviorPtBr: 'Patrulhamento em intervalos de aproximadamente 80 metros.',
    mapImageUrl: '',
    mapMarker: { x: 0.52, y: 0.48 },
    notes: [
        {
            variant: 'warning',
            textPtBr: 'Este personagem pode ser negociado em seu estado de pedra selada.',
            textEn: 'This fellow can be traded in its sealed stone state.',
        },
        {
            variant: 'info',
            textPtBr: 'Coleções regionais relacionadas a este personagem não estão implementadas no momento.',
            textEn: 'Regional collections for this fellow are not implemented yet.',
        },
        {
            variant: 'warning',
            textPtBr:
                'Bartolo é um companheiro que desaparece após 1 hora de aparecer — tenha cuidado ao tentar capturá-lo.',
            textEn: 'Bartolo disappears 1 hour after spawning — be careful when trying to capture it.',
        },
        {
            variant: 'info',
            textPtBr: 'Corre o boato de que Bartolo só aparece em certos momentos.',
            textEn: 'Rumor has it Bartolo only appears at certain times.',
        },
        {
            variant: 'info',
            textPtBr: 'Pode aprender até cinco habilidades passivas diferentes.',
            textEn: 'Can learn up to five different passive skills.',
        },
    ],
    statsNotePtBr:
        'Cada atributo inclui ajustes provenientes de habilidades passivas aprendidas pelo personagem no nível correspondente.',
    statsNoteEn:
        'Each stat includes adjustments from passive skills learned at the corresponding level.',
    stats: [
        { key: 'hp', labelPtBr: 'HP', labelEn: 'HP', lv1: '20962', lvMax: '80935' },
        { key: 'will', labelPtBr: 'Força de vontade', labelEn: 'Willpower', lv1: '6890', lvMax: '29842' },
        { key: 'dps', labelPtBr: 'DPS', labelEn: 'DPS', lv1: '914', lvMax: '3270' },
        { key: 'pdef', labelPtBr: 'Defesa física', labelEn: 'Physical defense', lv1: '2306', lvMax: '7531' },
        { key: 'alt', labelPtBr: 'Altitude máxima', labelEn: 'Max altitude', lv1: '1700', lvMax: '1700' },
        { key: 'speed', labelPtBr: 'Velocidade', labelEn: 'Movement speed', lv1: '15.00', lvMax: '15.00' },
        { key: 'trait', labelPtBr: 'Habilidade especial', labelEn: 'Special ability', lv1: '880', lvMax: '880' },
        { key: 'adv', labelPtBr: 'Aventureiro', labelEn: 'Adventurer', lv1: '100', lvMax: '100' },
    ],
    skills: [
        {
            namePtBr: 'Aura lendária',
            nameEn: 'Legendary Aura',
            skillType: 'passive',
            maxLevel: 1,
            effectPtBr:
                'Concedido apenas a personagens lendários — neutraliza efeitos negativos em todas as regiões.',
            effectEn: 'Granted only to legendary fellows — negates negative effects in all regions.',
        },
        {
            namePtBr: 'Aura de uma besta demoníaca',
            nameEn: 'Demonic Beast Aura',
            skillType: 'passive',
            maxLevel: 8,
            effectPtBr:
                '(mín.) ATQ +39%, HP +31%, DEF +22%, Vel. +25% | (máx.) ATQ +56%, HP +46%, DEF +38%, Vel. +25%',
            effectEn: 'Min/max attack, HP, defense and movement bonuses.',
        },
        {
            namePtBr: 'Esconderijo de monstro',
            nameEn: 'Monster Hide',
            skillType: 'passive',
            maxLevel: 1,
            effectPtBr:
                'Imune a efeitos negativos nas Planícies de Akrat e regiões de Turimulan; reduz parte do dano recebido.',
            effectEn: 'Immunity in Akrat/Turimulan regions; reduces damage taken.',
        },
        {
            namePtBr: 'A Alma de Bartolo (Floresta de Brakal)',
            nameEn: 'Soul of Bartolo (Brakal Forest)',
            skillType: 'passive',
            maxLevel: 1,
            effectPtBr: 'Aumenta ATQ, crítico e HP máximo na Floresta de Brakal.',
            effectEn: 'Increases attack, crit and max HP in Brakal Forest.',
        },
    ],
    activeSkills: [
        {
            namePtBr: 'Raio',
            nameEn: 'Lightning',
            skillType: 'Attack',
            maxLevel: 1,
            energy: '250',
            cast: '0,8 segundos',
            cooldown: '4 minutos',
            damage: '6000-10000',
            effectPtBr:
                'ATQ físico/mágico -10% por 10s; DEF mágica/física -20%; HP -800~1200/s por 10s.',
            effectEn: 'Debuffs attack, defense and HP over time.',
        },
    ],
    sealStone: [
        { key: 'atk', labelPtBr: 'Todo o poder de ataque', labelEn: 'All attack power', lv1: '187-205', lvMax: '371-389' },
        { key: 'crit', labelPtBr: 'Todos os críticos', labelEn: 'All crits', lv1: '243-267', lvMax: '385-409' },
        { key: 'stats', labelPtBr: 'Todas as estatísticas', labelEn: 'All stats', lv1: '20-23', lvMax: '27-30' },
    ],
    captureMaterials: [
        { namePtBr: 'Sangue Bragi de elite', quantity: '80' },
        { namePtBr: 'Cristal de Yatuman', quantity: '8' },
        { namePtBr: 'A Marca da Besta', quantity: '1' },
        { namePtBr: 'Colega empático de alto nível', quantity: '45' },
        { namePtBr: 'Poção de captura de primeira qualidade', quantity: '60' },
    ],
    dropSources: [{ itemPtBr: 'Prova da Besta Demoníaca Bartolo', monsterPtBr: 'Yatuman', level: '72' }],
    missionHints: [
        {
            materialPtBr: 'A Marca da Besta',
            missionPtBr: 'Missão repetível: Troca de Prova da Fera',
            detailsPtBr: 'Pedras seladoras de Plutão, Azure Cariat e Thunder Calliat.',
        },
        {
            materialPtBr: 'Cristal de Yatuman',
            missionPtBr: 'Missão diária: A vitória ainda está distante',
            detailsPtBr: 'Derrotando o Yatuman.',
        },
    ],
};

export function emptyStat(key = 'stat'): MountGuideStatRow {
    return { key, labelPtBr: '', labelEn: '', lv1: '', lvMax: '' };
}

export function emptySkill(): MountGuideSkill {
    return { namePtBr: '', nameEn: '', skillType: 'passive', effectPtBr: '', effectEn: '' };
}

export function emptyNote(): MountGuideNote {
    return { textPtBr: '', textEn: '', variant: 'info' };
}

export function emptyMaterial(): MountGuideCaptureMaterial {
    return { namePtBr: '', quantity: '' };
}

export function emptyDrop(): MountGuideDropSource {
    return { itemPtBr: '', monsterPtBr: '', level: '' };
}

export function emptyMission(): MountGuideMissionHint {
    return { materialPtBr: '', missionPtBr: '', detailsPtBr: '' };
}
